/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { nextTick } from 'vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import ErrorLayout from '@/layouts/ErrorLayout.vue'
import Configurator from '@/views/Configurator.vue'
import HomePage from '@/views/HomePage.vue'
import Knowledge from '@/views/Knowledge.vue'
import ConfiguratorList from '@/views/ConfiguratorList.vue'
import LiveSupervision from '@/views/LiveSupervision.vue'
import Integration from '@/views/Integration.vue'
import Profile from '@/views/Profile.vue'
import ErrorIdp from '@/views/ErrorIdp.vue'
import BlankView from '@/views/BlankView.vue'
import { useAuthBff } from '@/composables/bff/useAuthBff'
import { useNavigationPermissions } from '@/composables/permissions/useNavigationPermissions'
import type { RouteLocationNormalized } from 'vue-router'
import ColdMonitoring from '@/views/ColdMonitoring.vue'
import UsersList from '@/views/UsersList.vue'
import * as Sentry from '@sentry/vue'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import { setPageName } from '@/composables/analytics/useAnalytics'
import { config } from '@/config'

const supportedLocales = ['fr', 'en', 'de', 'es', 'it', 'nl', 'pt', 'ru']
const defaultLocale = 'en'

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: `/${defaultLocale}`,
  },
  {
    path: '/:locale(fr|en|de|es|it|nl|pt|ru)',
    component: DefaultLayout,
    children: [
      {
        path: '',
        component: HomePage,
        meta: { requiresAuth: true, breadcrumbs: false },
      },
      {
        path: 'virtual-agents',
        component: ConfiguratorList,
        meta: { requiresAuth: true, permission: 'virtual-agents', breadcrumbs: false },
      },
      {
        path: 'fine-tuning',
        component: BlankView,
        meta: { requiresAuth: true, permission: 'fine-tuning', breadcrumbs: false },
      },
      {
        path: 'variables',
        component: () => import('@/views/Variables.vue'),
        meta: { requiresAuth: true, permission: 'variables', breadcrumbs: false },
      },
      {
        path: 'configurator/:id?',
        component: Configurator,
        props: true,
        meta: { requiresAuth: true, permission: 'virtual-agents' },
        redirect: (to) => `${to.path}/general-information`,
        children: [
          {
            path: 'general-information',
            component: () => import('@/views/configurator/GeneralInformationTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - General Information',
            },
          },
          {
            path: 'conversational-flow',
            component: () => import('@/views/configurator/ConversationalFlowTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Conversational Flow',
            },
          },
          {
            path: 'intent-recognition',
            component: () => import('@/views/configurator/IntentRecognitionTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Intent Recognition',
            },
          },
          {
            path: 'knowledge',
            component: () => import('@/views/configurator/KnowledgeTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Knowledge',
            },
          },
          {
            path: 'call-limits',
            component: () => import('@/views/configurator/CallLimitsTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Call Limits',
            },
          },
          {
            path: 'tools',
            component: () => import('@/views/configurator/ToolsTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Tools',
            },
          },
          {
            path: 'experimental',
            component: () => import('@/views/configurator/ExperimentalTab.vue'),
            meta: {
              requiresAuth: true,
              permission: 'virtual-agents',
              pageName: 'Builder - Experimental',
            },
          },
        ],
      },
      {
        path: 'knowledge',
        component: Knowledge,
        meta: { requiresAuth: true, permission: 'knowledge', breadcrumbs: false },
      },
      {
        path: 'live-supervision',
        component: LiveSupervision,
        props: true,
        meta: { requiresAuth: true, permission: 'live-supervision', breadcrumbs: false },
      },
      {
        path: 'integration',
        component: Integration,
        props: true,
        meta: { requiresAuth: true, permission: 'integration' },
      },
      {
        path: 'users',
        component: UsersList,
        meta: { requiresAuth: true, permission: 'users', breadcrumbs: false },
      },
      {
        path: 'profile',
        component: Profile,
        meta: { requiresAuth: true, permission: 'profile', breadcrumbs: false },
      },
      {
        path: 'cold-monitoring',
        component: ColdMonitoring,
        props: true,
        meta: { requiresAuth: true, breadcrumbs: false },
      },

      {
        path: ':pathMatch(.*)*',
        component: ErrorLayout,
        children: [
          {
            path: '',
            name: 'LocaleNotFound',
            component: () => import('@/views/ErrorPage.vue'),

            meta: { requiresAuth: false, breadcrumbs: false },
          },
        ],
      },
    ],
  },

  {
    path: '/dev/live-supervision',
    name: 'DevLiveSupervision',
    component: () => import('@/views/dev/LiveSupervisionDebug.vue'),
    meta: { requiresAuth: false, localDevOnly: true },
  },
  {
    path: '/error',
    component: ErrorLayout,
    children: [
      {
        path: '',
        name: 'ErrorPage',
        component: () => import('@/views/ErrorPage.vue'),
        props: (route: RouteLocationNormalized) => ({
          title: route.query.title,
          description: route.query.desc,
          errorCode: route.query.code,
        }),
        meta: { requiresAuth: false },
      },
    ],
  },

  {
    path: '/error-idp',
    name: 'ErrorIdp',
    component: ErrorIdp,
    meta: { requiresAuth: false },
  },

  {
    path: '/:pathMatch(.*)*',
    component: ErrorLayout,
    children: [
      {
        path: '',
        name: 'NotFound',
        component: () => import('@/views/ErrorPage.vue'),

        meta: { requiresAuth: false },
      },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  if (to.meta.localDevOnly) {
    const { isLocalDev } = useFeatureFlags()
    if (!isLocalDev) return { name: 'NotFound', replace: true }
  }

  if (to.meta.requiresAuth === false) return true

  const { checkAuth, isAuthenticated, login, primaryLanguage } = useAuthBff()
  const identityToken = new RegExp(/identityToken=([^&]+)/).exec(to.hash)?.[1]

  try {
    if (identityToken) {
      const success = await login(identityToken)
      if (success) {
        await checkAuth()
        resetRedirectCount()

        const redirectUrl = to.query.redirect as string
        if (redirectUrl) {
          window.location.assign(redirectUrl)
          return false
        }

        // Use primary_language from token to set the locale
        const tokenLocale = primaryLanguage.value
        const targetLocale =
          tokenLocale && supportedLocales.includes(tokenLocale) ? tokenLocale : defaultLocale
        const currentLocale = to.params.locale as string | undefined
        const targetPath =
          currentLocale && currentLocale !== targetLocale
            ? to.path.replace(`/${currentLocale}`, `/${targetLocale}`)
            : to.path.startsWith(`/${targetLocale}`)
              ? to.path
              : `/${targetLocale}`

        const cleanQuery = { ...to.query }
        delete cleanQuery.identityToken
        const hasRemainingQuery = Object.keys(cleanQuery).length > 0

        return {
          path: targetPath,
          query: hasRemainingQuery ? cleanQuery : undefined,
          replace: true,
        }
      } else {
        if (getRedirectCount() < 3) {
          incrementRedirectCount()
          redirectToIdp(to.fullPath)
          return false
        }
        resetRedirectCount()
        return { name: 'ErrorIdp' }
      }
    }

    if (!isAuthenticated.value) {
      await checkAuth()
      await nextTick()
    }
    if (isAuthenticated.value) {
      // Identify user in Sentry for error correlation
      const { isPreProduction } = useFeatureFlags()
      if (isPreProduction) {
        const { userId } = useAuthBff()
        Sentry.setUser({
          id: userId.value || undefined,
        })
      }

      const locale = to.params.locale as string | undefined
      if (!locale || !supportedLocales.includes(locale)) {
        const normalizedPath = to.fullPath.startsWith('/') ? to.fullPath : `/${to.fullPath}`
        if (!normalizedPath.startsWith(`/${defaultLocale}/`)) {
          return {
            path: `/${defaultLocale}${normalizedPath}`,
            replace: true,
          }
        }
      }

      // Override URL locale with the user's preferred language
      const preferredLocale = primaryLanguage.value
      if (
        locale &&
        preferredLocale &&
        supportedLocales.includes(preferredLocale) &&
        locale !== preferredLocale
      ) {
        return {
          path: to.path.replace(`/${locale}`, `/${preferredLocale}`),
          query: Object.keys(to.query).length > 0 ? to.query : undefined,
          replace: true,
        }
      }

      // Check navigation permissions
      if (typeof to.meta.permission === 'string') {
        const { hasPermission } = useNavigationPermissions()

        if (!hasPermission(to.meta.permission)) {
          console.warn(
            `Access denied to route: ${to.path} (missing permission: ${to.meta.permission})`,
          )
          // Redirect to home page with the same locale
          const redirectLocale = locale ?? defaultLocale
          return {
            path: `/${redirectLocale}`,
            replace: true,
          }
        }
      }

      return true
    } else {
      redirectToIdp(to.fullPath)
      return false
    }
  } catch (error) {
    console.error('Error during authentication verification:', error)
    redirectToIdp(to.fullPath)
    return false
  }
})

router.afterEach((to) => {
  const pageName = to.meta.pageName
  if (typeof pageName === 'string') setPageName(pageName)
})

/* ----------------- HELPERS ----------------- */

function redirectToIdp(targetPath: string) {
  const currentUrl = `${window.location.host}${targetPath}`
  const idpUrl = config.idpUrl

  if (!idpUrl) {
    console.error('Missing env VITE_IDP_URL')
    return
  }

  window.location.assign(`${idpUrl}?url=${encodeURIComponent(currentUrl)}&scope=virtual-agent`)
}

function getRedirectCount(): number {
  return Number(sessionStorage.getItem('idpRedirectCount') ?? 0)
}

function incrementRedirectCount() {
  const current = getRedirectCount()
  sessionStorage.setItem('idpRedirectCount', String(current + 1))
}

function resetRedirectCount() {
  sessionStorage.removeItem('idpRedirectCount')
}
